import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type PropsWithChildren,
} from "react";
import { useNavigate } from "react-router-dom";

import getAppConfig, { env } from "@liberty-fund/config";
import { Logger } from "@liberty-fund/logger";
import { setAccessTokenProvider } from "@liberty-fund/utils/auth";

import { analytics } from "@/analytics";
import Unauthorized from "@/routes/Unauthorized";
import { Loader } from "@/ui/components";

/**
 * Auth0 SPA authentication, replacing the AWS load balancer's OIDC integration.
 *
 * The ALB used to terminate authentication and hand the container
 * `x-amzn-oidc-*` headers; a static SPA never sees those, so the browser now
 * runs the Authorization Code + PKCE flow itself.
 *
 * Tokens are persisted, so a reload restores the session from the stored
 * refresh token without contacting Auth0 interactively. See the comment on
 * `cacheLocation` below for why the in-memory alternative was abandoned.
 */
export default function AuthProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const config = getAppConfig();

  if (!config.auth0.clientId) {
    // Say what's wrong rather than bouncing the user to Auth0 with an empty
    // client_id, which surfaces as an opaque "Unknown client" error page.
    // Rendered, not thrown: an uncaught throw here is a blank white page.
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-lf-red-dark">
          Authentication is not configured
        </h1>
        <p className="pt-2 max-w-2xl text-lf-blue-dark">
          <code>auth0.clientId</code> is empty for the <code>{env}</code>{" "}
          environment in <code>packages/config/config.json</code>. This build
          needs a dedicated Auth0 <strong>Single Page Application</strong>{" "}
          client — the former load-balancer client is a Regular Web Application
          and cannot complete the PKCE flow.
        </p>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/callback`,
        audience: config.auth0.audience,
        scope: config.auth0.scope,
      }}
      // Tokens are persisted so a reload restores the session from the stored
      // refresh token over a plain XHR: no round trip through Auth0, no hidden
      // iframe, and nothing that behaves differently depending on the domain.
      //
      // An in-memory cache was tried first, on the grounds that an XSS bug then
      // could not exfiltrate a refresh token. It does not survive a reload,
      // though, which leaves only two ways to restore a session — and both are
      // worse. A full redirect on every reload is the visible round trip that
      // gets reported as "it made me log in again". The hidden-iframe check
      // (`useRefreshTokensFallback`) is worse still: it needs a third-party
      // cookie, so it fails outright wherever the portal and the Auth0 domain
      // are not same-site, and it fails by *hanging* for
      // `authorizeTimeoutInSeconds` — 60 seconds of "Signing in" before it
      // gives up and redirects anyway.
      //
      // The trade is real: a refresh token in localStorage is reachable by
      // injected script. Refresh-token rotation is what bounds the damage, and
      // it is required in the Auth0 client setup (docs/CLOUD_API_REQUIREMENTS.md).
      cacheLocation="localstorage"
      useRefreshTokens
      // No iframe fallback. With a persisted refresh token it is only reached
      // when that token is already dead, where a redirect is both correct and
      // immediate — and it is the sole way this app can hang on start-up.
      useRefreshTokensFallback={false}
      onRedirectCallback={(appState) => {
        // A completed login is a fresh session, so the next time it dies it
        // gets its own automatic re-authentication. Waiting for the first
        // successful token request to reset this (as the accessor below also
        // does) leaves the guard armed on any page that never makes one.
        markReauthAttempted(false);
        // Restore the route the user originally asked for, dropping the
        // ?code=&state= querystring Auth0 appended.
        navigate(appState?.returnTo ?? "/", { replace: true });
      }}
    >
      <RegisterAccessTokenProvider />
      {children}
    </Auth0Provider>
  );
}

/**
 * Latest Auth0 state, kept outside React so the accessor below can read it
 * without being re-registered.
 */
let latestAuth0: {
  getAccessTokenSilently?: () => Promise<string>;
  loginWithRedirect?: (options?: {
    appState?: { returnTo?: string };
  }) => Promise<void>;
  isAuthenticated: boolean;
} = { isAuthenticated: false };

/**
 * Auth0 error codes that mean the stored session is gone for good, as opposed
 * to a request that merely failed and could succeed on retry.
 *
 * `invalid_grant` is what Auth0 returns for "Unknown or invalid refresh
 * token" — the portal's SPA clients use rotating refresh tokens, so a token
 * that was already rotated away (or whose grant family was revoked after a
 * replay) can never be redeemed again.
 */
const TERMINAL_AUTH_ERRORS = new Set([
  "invalid_grant",
  "missing_refresh_token",
  "login_required",
  "consent_required",
]);

function isTerminalAuthError(error: unknown): boolean {
  const code = (error as { error?: string })?.error;
  return typeof code === "string" && TERMINAL_AUTH_ERRORS.has(code);
}

/**
 * Guards against a redirect storm. Every in-flight request discovers the dead
 * session at roughly the same moment, and each one would otherwise call
 * `loginWithRedirect` — so the first caller wins and the rest just fail their
 * own request while the navigation happens.
 */
let reauthenticating = false;

/**
 * Survives the redirect, which the flag above cannot: navigating away reloads
 * the module and resets it. Without this, a session that is still broken when
 * the user lands back here would bounce them to Auth0 again, forever. One
 * automatic attempt is worth making; a second means re-authenticating isn't
 * the fix, and an error the user can see beats an invisible loop.
 */
const REAUTH_ATTEMPTED_KEY = "lf.portal.reauthAttempted";

function hasAttemptedReauth(): boolean {
  try {
    return window.sessionStorage.getItem(REAUTH_ATTEMPTED_KEY) === "true";
  } catch {
    // Storage can throw in private/sandboxed contexts. Treat it as "not yet
    // attempted": one redirect is the behaviour worth preserving.
    return false;
  }
}

function markReauthAttempted(attempted: boolean): void {
  try {
    if (attempted) {
      window.sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, "true");
    } else {
      window.sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
    }
  } catch {
    // Not being able to record the attempt is not worth failing a request over.
  }
}

/**
 * Set once the session is known to be dead and an automatic redirect has
 * already been spent on it, so `RequireAuth` can say so instead of rendering
 * the app.
 *
 * This is the state PAD-429 got stuck in. `isAuthenticated` keeps reporting
 * `true` — auth0-spa-js derives it from the cached ID token, which outlives
 * the refresh token — so without this the app rendered normally with every
 * request going out unauthenticated: empty lists, no error, no way back to
 * the login page. The re-auth guard survives a reload, so refreshing didn't
 * help either. Leaving Auth0's login page without signing in (Back, a
 * restored tab) was enough to get there.
 *
 * Lives outside React because the token accessor that discovers it does.
 */
let sessionExpired = false;
const sessionExpiredListeners = new Set<() => void>();

function markSessionExpired(): void {
  if (sessionExpired) return;
  sessionExpired = true;
  sessionExpiredListeners.forEach((listener) => listener());
}

function subscribeSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => sessionExpiredListeners.delete(listener);
}

function getSessionExpired(): boolean {
  return sessionExpired;
}

/**
 * Auth0 reports the post-login Action's `api.access.deny()` the same way it
 * reports any other rejected authorization request: an OAuth `access_denied`
 * error on the callback. That's the one case worth a dedicated screen rather
 * than the generic "Unable to sign in" fallback below.
 *
 * The redirect carries only `error`/`error_description` — no ID token, since
 * the Action rejects before one is issued — so there is no email to show on
 * the Unauthorized screen from this alone. Getting the actual attempted
 * email requires cloud-auth's Action to embed it in the deny reason and
 * cloud-apps to parse it back out; until that lands, the screen renders
 * without one.
 */
function isAccessDenied(error: unknown): boolean {
  return (error as { error?: string })?.error === "access_denied";
}

/**
 * cloud-auth's post-login Action prefixes every `api.access.deny()` reason
 * with `org.libertyfund.{info|error}:` so its other client (Concierge)
 * can pick a visual treatment before displaying the rest verbatim (see
 * `user-provisioning.ts`'s `DENY_MESSAGES` comment). Portal doesn't yet
 * distinguish the two treatments, but the prefix isn't part of the message
 * and shouldn't leak into the UI here either.
 */
function getDenyMessage(error: unknown): string | undefined {
  const description = (error as { error_description?: string })
    ?.error_description;
  const message = description
    ?.replace(/^org\.libertyfund\.(?:info|error):\s*/, "")
    .trim();
  return message || undefined;
}

/**
 * Sends the user back through Auth0 to establish a new session.
 *
 * Before this existed, a dead refresh token was swallowed and reported as
 * "no token": `isAuthenticated` stays `true` because auth0-spa-js caches the
 * user independently of the refresh token, so `RequireAuth` never re-prompted
 * and every subsequent request went out with no Authorization header — for
 * the rest of the session, and across reloads, since the dead token is
 * restored from localStorage. The ALB used to handle this by redirecting on
 * expiry; owning the session in the browser means owning that branch too.
 */
function reauthenticate(): void {
  if (reauthenticating) return;

  if (hasAttemptedReauth()) {
    Logger.error(
      "Session still invalid after re-authenticating; not redirecting again",
    );
    // Not redirecting again is the loop guard; showing nothing is not. Hand
    // the decision to the user, who can't loop by accident.
    markSessionExpired();
    return;
  }

  const { loginWithRedirect } = latestAuth0;
  if (!loginWithRedirect) return;

  reauthenticating = true;
  markReauthAttempted(true);
  Logger.info("Session is no longer valid; re-authenticating");
  analytics.track(
    "Login Started",
    { trigger: "reauth" },
    { beforeUnload: true },
  );

  void loginWithRedirect({
    appState: {
      returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
    },
  }).catch((redirectError: unknown) => {
    // Let a later request try again rather than stranding the app with no
    // token and no way back.
    reauthenticating = false;
    markReauthAttempted(false);
    Logger.error("Failed to re-authenticate after session loss", {
      message: (redirectError as Error)?.message,
    });
  });
}

/**
 * Back from Auth0's login page can restore this page from the back/forward
 * cache rather than reloading it, and that restores `reauthenticating = true`
 * along with everything else. Every later token failure would then return at
 * the first guard in `reauthenticate()` — before it can mark the session
 * expired — leaving the broken app on screen until a manual reload.
 *
 * A login that completes comes back through `/callback` as a fresh page load,
 * so a restored page with the latch still set means the user left Auth0
 * without signing in: the automatic redirect is spent, and the session is
 * still dead. The sessionStorage guard stays set, so this never redirects
 * again on its own.
 */
window.addEventListener("pageshow", (event) => {
  if (!event.persisted || !reauthenticating) return;
  reauthenticating = false;
  if (hasAttemptedReauth()) markSessionExpired();
});

/**
 * Registered at module scope, before any component mounts, so there is never a
 * window in which a request can find no accessor at all.
 */
setAccessTokenProvider(async () => {
  const { getAccessTokenSilently, isAuthenticated } = latestAuth0;
  if (!isAuthenticated || !getAccessTokenSilently) return undefined;

  try {
    const token = await getAccessTokenSilently();
    // A working token means any earlier recovery succeeded, so the next dead
    // session gets its own attempt rather than inheriting this one's.
    if (hasAttemptedReauth()) markReauthAttempted(false);
    return token;
  } catch (error) {
    if (isTerminalAuthError(error)) {
      reauthenticate();
    } else {
      // Transient — a network blip, or auth0-spa-js's 5s token lock timing
      // out under load. The caller's own retry can still succeed.
      Logger.warn("Failed to acquire access token", {
        message: (error as Error)?.message,
      });
    }
    return undefined;
  }
});

/**
 * Publishes Auth0's token accessor to the non-React consumers (GraphQL client,
 * authenticated asset loader) that can't call `useAuth0()` themselves.
 *
 * The update happens during render, not in an effect. SWR starts its first
 * request from a layout effect, and React runs every layout effect before any
 * passive effect — so an effect here would lose the race on the commit that
 * first renders the app authenticated, and that request would go out with no
 * Authorization header, fail, and only succeed on a later revalidation.
 *
 * This component is rendered before `children`, so by the time anything below
 * it renders (let alone runs an effect) the accessor already reflects the
 * current session.
 */
function RegisterAccessTokenProvider() {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } =
    useAuth0();

  latestAuth0 = { getAccessTokenSilently, isAuthenticated, loginWithRedirect };

  return null;
}

/**
 * Gate that mirrors the old ALB behaviour: every route required a session, so
 * an unauthenticated visitor is sent straight to Auth0 rather than shown a
 * login page.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const [stalled, setStalled] = useState(false);
  const navigate = useNavigate();
  const isSessionExpired = useSyncExternalStore(
    subscribeSessionExpired,
    getSessionExpired,
  );

  const startSignIn = useCallback(
    (trigger: "auto" | "retry" | "session_expired") => {
      setStalled(false);
      analytics.track("Login Started", { trigger }, { beforeUnload: true });
      void loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        },
      }).catch((redirectError: unknown) => {
        Logger.error("Failed to start sign-in", {
          message: (redirectError as Error)?.message,
        });
        setStalled(true);
      });
    },
    [loginWithRedirect],
  );

  useEffect(() => {
    if (isLoading || isAuthenticated || error) return;
    startSignIn("auto");
  }, [error, isAuthenticated, isLoading, startSignIn]);

  /**
   * Signing in either restores the session or navigates away, and both are
   * quick. An indefinite spinner is the one outcome a user can neither
   * understand nor escape — a 60-second silent-auth timeout produced exactly
   * that — so it gets a deadline and a way out rather than being trusted to
   * always resolve.
   */
  useEffect(() => {
    if (isAuthenticated || error || stalled) return;

    const timer = window.setTimeout(() => setStalled(true), 20_000);
    return () => window.clearTimeout(timer);
  }, [isAuthenticated, error, stalled]);

  /**
   * Auth0's OAuth transaction is single-use: `handleRedirectCallback()`
   * deletes it the instant it's read, whether that read succeeds or fails.
   * On success `onRedirectCallback` (above, in `AuthProvider`) navigates away
   * from `/callback` and the spent `?code=&state=` never comes back. On
   * failure nothing does — this component decides what to render from
   * `error` in memory, not from the URL, so the browser is left sitting on
   * the exact `/callback?error=...` URL that just failed.
   *
   * Left alone, a refresh of that URL — or "Try again" capturing it via
   * `window.location` as `returnTo` — replays the same spent transaction and
   * fails a SECOND time with an unrelated "Invalid state" error instead of
   * the original one. Clearing the URL as soon as the error lands prevents
   * both; the error itself stays in React state, so the right screen keeps
   * rendering regardless of what the address bar says.
   *
   * `error` here is shared, app-wide Auth0 state, not scoped to this one
   * redirect — a background `getAccessTokenSilently()` hiccup elsewhere in
   * the app sets the exact same field and never gets cleared by a later
   * success. So this only fires for a URL that's actually the spent
   * callback params themselves (mirroring auth0-react's own check for
   * whether a URL is a redirect callback at all) — never for an
   * unrelated `error` colliding with an ordinary page's own query string
   * or hash.
   */
  useEffect(() => {
    if (!error) return;
    const params = new URLSearchParams(window.location.search);
    const isSpentOAuthCallback =
      (params.has("code") || params.has("error")) && params.has("state");
    if (isSpentOAuthCallback) {
      navigate("/", { replace: true });
    }
  }, [error, navigate]);

  // Ahead of `isAuthenticated`, which is still `true` here — that stale flag is
  // the whole problem (see `sessionExpired`).
  if (isSessionExpired) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-lf-blue-dark">
          Your session has expired
        </h1>
        <p className="pt-2 max-w-2xl text-lf-blue-dark">
          Sign in again to continue where you left off.
        </p>
        {stalled && (
          // `startSignIn` sets `stalled` when the redirect itself fails; this
          // screen renders ahead of the generic failure UI, so say so here.
          <p role="alert" className="pt-2 max-w-2xl text-lf-red-dark">
            Signing in did not start. This usually means the browser could not
            reach Auth0. Try again.
          </p>
        )}
        <button
          type="button"
          onClick={() => startSignIn("session_expired")}
          className="mt-4 rounded-md bg-lf-blue-dark px-4 py-2 text-white"
        >
          Sign in
        </button>
      </div>
    );
  }

  // Checked before the failure states, and deliberately so: a session that
  // arrives after the deadline has passed still wins. Ordering this below them
  // would strand a signed-in user on an error page for the rest of the session.
  if (isAuthenticated) {
    return <>{children}</>;
  }

  if (isAccessDenied(error)) {
    // Auth0 never delivers the attempted email on a denied login — the
    // post-login Action rejects before any token is issued, so there is no
    // user info for the SPA to read. Nothing trustworthy to show here; see
    // the note on `isAccessDenied` for the real fix.
    return <Unauthorized message={getDenyMessage(error)} />;
  }

  if (error || stalled) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-lf-red-dark">
          Unable to sign in
        </h1>
        <p className="pt-2 max-w-2xl text-lf-blue-dark">
          {error
            ? error.message
            : "Signing in did not complete. This usually means the browser could not reach Auth0."}
        </p>
        <button
          type="button"
          onClick={() => startSignIn("retry")}
          className="mt-4 rounded-md bg-lf-blue-dark px-4 py-2 text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="py-10">
      <Loader text="Signing in" />
    </div>
  );
}
